import { inject, Injectable } from '@angular/core';
import { CanActivateFn } from '@angular/router';
import { Store } from '@ngxs/store';
import { Observable } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import { AppStateModel, selectAppState } from '../../../app.state';
import { RouteNavigationService } from '../route-navigation.service';

@Injectable()
export class SessionCheckRouteGuardLogic {
  constructor(
    private _store: Store,
    private _routeNavigationService: RouteNavigationService,
  ) {
  }

  public canActivate(
    letTroughCheck: (state: AppStateModel) => boolean,
    fallbackAction: (routeNavigationService: RouteNavigationService, state: AppStateModel) => void,
  ): Observable<boolean> {
    return selectAppState(this._store).pipe(
      filter((state: AppStateModel) => !!state?.isInitialized),
      take(1),
      map((applicationState: AppStateModel) => {
        if (!letTroughCheck(applicationState)) {
          fallbackAction(this._routeNavigationService, applicationState);

          return false;
        }

        return true;
      }),
    );
  }
}

export const LoggedInRouteGuard: CanActivateFn = (): Observable<boolean> => {
  return inject(SessionCheckRouteGuardLogic).canActivate(
    state => !!state.session,
    nav => nav.goToLoginAsync(true),
  );
};

export const ValidatedEmailRouteGuard: CanActivateFn = (): Observable<boolean> => {
  return inject(SessionCheckRouteGuardLogic).canActivate(
    state => !!state.session?.user.isEmailValidated,
    nav => nav.goToEmailValidationAsync(),
  );
};

export const NotValidatedEmailRouteGuard: CanActivateFn = (): Observable<boolean> => {
  return inject(SessionCheckRouteGuardLogic).canActivate(
    state => !state.session?.user.isEmailValidated,
    nav => nav.goToDashboardAsync(),
  );
};

export const NotLoggedInRouteGuard: CanActivateFn = (): Observable<boolean> => {
  return inject(SessionCheckRouteGuardLogic).canActivate(
    state => !state.session,
    nav => nav.goToDashboardAsync(),
  );
};

export const HasCompanyRouteGuard: CanActivateFn = (): Observable<boolean> => {
  return inject(SessionCheckRouteGuardLogic).canActivate(
    state => !!state.session?.company,
    nav => nav.goToCompanyCreationAsync(),
  );
};

export const NoCompanyRouteGuard: CanActivateFn = (): Observable<boolean> => {
  return inject(SessionCheckRouteGuardLogic).canActivate(
    state => !state.session?.company,
    nav => nav.goToDashboardAsync(),
  );
};
